import React, { useEffect, useRef } from 'react';
import { Box, useColorModeValue, usePrefersReducedMotion, useToast } from '@chakra-ui/react';
import { unwrapResult } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useReduxDispatch } from '../../../app/store';
import { ArcButton } from '../../../common/components/ArcButton';
import { ContactSupportButton } from '../../../common/components/ContactSupportButton';
import { trackChargingForceStarted, trackChargingStopped } from '../../../utils/common/analytics';
import {
  trackDynatraceChargerControlOpened,
  trackDynatraceChargingForceStarted,
  trackDynatraceChargingForceStartedError,
  trackDynatraceChargingStopped,
  trackDynatraceChargingStoppedError,
} from '../../../utils/native/dynatrace-events';
import { selectSite } from '../../site/siteSlice';
import { selectFirmwareUpdateStatus } from '../../smart-amp/evseStreamSlice';
import { useSmartAmpSse } from '../../smart-amp/useSmartAmpSse';
import {
  deriveEvseDeviceActive,
  selectEvChargerData,
  selectEvseType,
  startCharging,
  stopCharging,
} from './evChargerSlice';

const COLOR_GREEN = '#3DCD58'; // arc accent
const SUPPORT_PATH = '../../account/support?direction=back&backURL=../../home/device-status';
const WIFI_CONFIG_PATH = '../device-wifi-configuration?direction=forward&backURL=../device-status';

// motion(Box) rather than <Box as={motion.div}>: Chakra's `transition` style prop would otherwise swallow
// framer-motion's `transition` object. Also gives correct `children` typing under React 19 + framer-motion 6.
const MotionBox = motion(Box);

export const ChargingControl = () => {
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const {
    isLoaded,
    isUpdating,
    siteDeviceId,
    status,
    chargeSimulationType,
    control_mode: controlMode,
  } = useSelector(selectEvChargerData);
  const { cloudFirmwareStatus } = useSelector(selectFirmwareUpdateStatus);
  const { loadActive, tempActive } = useSmartAmpSse();
  const evseType = useSelector(selectEvseType);
  const { isSmartAmpActive, isSmartPlugActive } = deriveEvseDeviceActive(
    evseType,
    chargeSimulationType,
    loadActive,
    tempActive
  );
  const isEvseDeviceActive = isSmartAmpActive || isSmartPlugActive;
  const site = useSelector(selectSite);
  const buttonFill = useColorModeValue('#505559', '#626469');
  const prefersReducedMotion = usePrefersReducedMotion();
  const hasTrackedControlOpen = useRef(false);

  useEffect(() => {
    if (!isLoaded || hasTrackedControlOpen.current) return;

    trackDynatraceChargerControlOpened();
    hasTrackedControlOpen.current = true;
  }, [isLoaded]);

  // Active firmware update states disable the charging control.
  // Use cloudFirmwareStatus (high-level aggregate) to determine if an update is in progress:
  // both 'Pending' and 'InProgress' mean the update is in progress.
  const isFirmwareUpdateActive = cloudFirmwareStatus === 'Pending' || cloudFirmwareStatus === 'InProgress';

  const showErrorToast = (error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        category: 'feature',
        feature: 'ev-charger',
        component: 'ChargingControl',
        method: 'handleCharging',
      },
      extra: { siteDeviceId, status },
    });
    toast({
      title: t('EVSE.command failed'),
      status: 'error',
      isClosable: true,
    });
  };

  async function handleStop() {
    try {
      unwrapResult(await dispatch(stopCharging(siteDeviceId)));
      trackChargingStopped();
      trackDynatraceChargingStopped();
    } catch (error) {
      trackDynatraceChargingStoppedError(error instanceof Error ? error.message : 'stop_charging_failed');
      showErrorToast(error);
    }
  }

  async function handleStart() {
    try {
      unwrapResult(await dispatch(startCharging(siteDeviceId)));
      trackChargingForceStarted();
      trackDynatraceChargingForceStarted();
    } catch (error) {
      trackDynatraceChargingForceStartedError(error instanceof Error ? error.message : 'start_charging_failed');
      showErrorToast(error);
    }
  }

  if (site.commissioning_status !== 'PENDING' && !isLoaded) {
    return null;
  }

  // Shared styling for the arc-style control buttons.
  const arcButtonProps = {
    'data-testid': 'ev-charging-control-button',
    mt: 6,
    w: '100%',
    arcColor: COLOR_GREEN,
    background: buttonFill,
    _hover: { background: buttonFill },
    color: 'white',
    borderColor: buttonFill,
    sx: { '> div': { background: `${buttonFill} !important` } },
  } as const;

  const disabledChargeNowButton = (
    <ArcButton {...arcButtonProps} isDisabled={true}>
      {t('EVSE.charge now')}
    </ArcButton>
  );

  // The single control for the current status (or null when the status has none).
  // Returned as a node so the wrapper below can smoothly collapse/expand the slot
  // when a button appears or disappears — keeping the charger above from jumping.
  const controlButton = (() => {
    // During an active firmware update we always show the disabled Charge Now
    // button, independent of charger status.
    if (isFirmwareUpdateActive) {
      return disabledChargeNowButton;
    }

    switch (status) {
      case 'Available':
      case 'SuspendedEV':
        return disabledChargeNowButton;
      case 'Charging':
        return (
          <ArcButton {...arcButtonProps} onClick={handleStop} isLoading={isUpdating}>
            {t('EVSE.stop charging')}
          </ArcButton>
        );
      case 'SuspendedEVSE':
        return (
          <ArcButton
            {...arcButtonProps}
            onClick={handleStart}
            isLoading={isUpdating}
            isDisabled={isEvseDeviceActive && controlMode !== 'Manual'}
          >
            {t('EVSE.charge now')}
          </ArcButton>
        );
      case 'Faulted':
        return <ContactSupportButton path={SUPPORT_PATH} w="100%" />;
      case 'Unavailable_Disconnect':
        return (
          <ArcButton {...arcButtonProps} onClick={() => navigate(WIFI_CONFIG_PATH)}>
            {t('EVSE.check wifi')}
          </ArcButton>
        );
      default:
        return null;
    }
  })();

  return (
    <AnimatePresence initial={false}>
      {controlButton && (
        <MotionBox
          key="ev-charging-control-slot"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          {controlButton}
        </MotionBox>
      )}
    </AnimatePresence>
  );
};
